import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

/**
 * Bronson Logo component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-logo.html
 *
 * The props can be mixed and matched if need be.
 *
 * @param {string} [alt] - Alternative image text.
 * @param {string} [ariaLabel] - Value for the `[aria-label]` attribute.
 * @param {string} [breakpoint] - Breakpoint to switch between small and large logo images.
 * @param {string} [className] - Additional CSS classes on the component element.
 * @param {boolean} [disableLink] - Disable any logo interaction by replacing the default `<a>` with `<span>`.
 * @param {function} [onClick] - Callback function on click.
 * @param {('bentley'|'cupra'|'lamborghini'|'porsche'|'dasweltauto'|'dasweltauto-es'|'logpay'|'commercial-vehicle-de'|'commercial-vehicle-en'|'commercial-vehicle-nl'|'commercial-vehicle-fr'|'commercial-vehicle-es')} [specificLogo] - Convenience prop to set specific brand logo as background image.
 * @param {string} [src] - Source of the logo image, when {@link specificLogo} is not used.
 * @param {string} [srcSet] - Set of sources of the logo image.
 * @param {string} [title] - The `[title]` attribute on the component element.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @return {JSX.Element} - The Logo component.
 * @constructor
 */
export function Logo({
  alt,
  ariaLabel,
  breakpoint,
  className,
  disableLink,
  onClick,
  specificLogo,
  src,
  srcSet,
  title,
  testId,
  ...otherProps
}) {
  const aClassNameList = classNames(
    'c-logo',
    {
      'c-logo--has-image': src,
      [`c-logo--${specificLogo}`]: specificLogo,
    },
    className
  ).trim()

  /**
   * Conditionally render a `<span>` as Logo wrapper.
   * @type {string}
   */
  const LogoElement = disableLink ? 'span' : 'a'

  return (
    <LogoElement
      className={aClassNameList}
      /**
       * Display the `[title]` conditionally depending on
       * if {@link disableLink} is not set or {@link title} is set.
       */
      title={disableLink ? null : title}
      data-testid={testId}
      aria-label={src ? null : ariaLabel}
      role={disableLink ? null : 'link'} // Improving accessibility through semantic mapping.
      onClick={disableLink ? null : onClick}
      {...otherProps}
    >
      {src && (
        <picture>
          {breakpoint && srcSet && <source srcSet={srcSet} media={`(max-width: ${breakpoint})`} />}
          <img src={src} alt={alt || ''} className="c-logo__image" />
        </picture>
      )}
    </LogoElement>
  )
}

Logo.propTypes = {
  alt: PropTypes.string,
  ariaLabel: PropTypes.string,
  breakpoint: PropTypes.string, // Bronson template: 'logoBreakpoint'.
  className: PropTypes.string,
  disableLink: PropTypes.bool, // Bronson template: 'logoDisableLink'.
  onClick: PropTypes.func,
  specificLogo: PropTypes.oneOf([
    'bentley',
    'commercial-vehicle-de',
    'commercial-vehicle-en',
    'commercial-vehicle-es',
    'commercial-vehicle-fr',
    'commercial-vehicle-nl',
    'cupra',
    'dasweltauto',
    'dasweltauto-es',
    'lamborghini',
    'logpay',
    'porsche',
  ]), // Convenience prop. Bronson template: 'modifier'.
  src: PropTypes.string, // Bronson template: 'logoPath'.
  srcSet: PropTypes.string, // Bronson template: 'logoSmallPath'.
  title: PropTypes.string,
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
}
